import { useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import {
  ArrowLeft, Loader2, Sprout, BookOpen, Gauge, Award, ShieldQuestion,
  Briefcase, Building2, Lightbulb, GraduationCap, Code, PenTool,
  Languages, Clock, Terminal, ArrowRight, Music, Compass, Check,
  Newspaper, Palette, Wrench, Store, Rocket, Sparkles, FastForward, Layers,
  Globe, Flag, Image, Video, Camera, Users, Megaphone, BarChart3,
  ShoppingCart, Truck, Target, Scissors, Hammer, FlaskConical,
  Calculator, Headphones, HeartPulse, Scale, Cpu, Workflow, Smartphone,
  Database, Server, Globe2, Brush, FileText, Factory, Home, UtensilsCrossed, Stethoscope,
} from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { trackLead } from "@/lib/analytics";
import { trackAppEvent } from "@/lib/pmf";
import { savePendingQuiz } from "@/lib/pendingQuiz";
import { useAuth } from "@/context/AuthContext";
import { Logo } from "@/components/Navbar";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";

const KNOWLEDGE = {
  key: "knowledge", title: "How much have you used AI?", help: "Be honest — we'll start you in exactly the right place.",
  options: [
    { value: "never", label: "Never used it & a bit hesitant", icon: ShieldQuestion },
    { value: "new", label: "New but curious", icon: Sprout },
    { value: "sometimes", label: "I use it sometimes", icon: Gauge },
    { value: "comfortable", label: "Comfortable", icon: BookOpen },
    { value: "advanced", label: "Advanced", icon: Award },
  ],
};

const GOAL = {
  key: "goal", title: "What do you mainly want from AI?", help: "We'll emphasize the right track for you.",
  options: [
    { value: "work", label: "Use AI in my job", icon: Building2 },
    { value: "business", label: "Run or grow my business", icon: Briefcase },
    { value: "job", label: "Get a job or a new skill", icon: GraduationCap },
    { value: "creative", label: "Creative work", icon: Palette },
    { value: "stay_updated", label: "Stay up to date with what's new", icon: Newspaper },
    { value: "curiosity", label: "General curiosity", icon: Lightbulb },
  ],
};

const PROFESSION = {
  key: "profession", title: "What do you do?", help: "This shapes the tools, examples and tone — pick the closest, or type your own.",
  options: [
    { value: "student", label: "Student / fresher", icon: GraduationCap },
    { value: "worker", label: "Salaried / office job", icon: Building2 },
    { value: "professional", label: "Licensed professional (CA, doctor, lawyer…)", icon: Stethoscope },
    { value: "business", label: "Business owner", icon: Store },
    { value: "freelancer", label: "Freelancer / self-employed", icon: Briefcase },
    { value: "creative", label: "Artist / creator / musician", icon: Palette },
    { value: "trade", label: "Trade / service (plumber, salon, shop…)", icon: Wrench },
    { value: "teacher", label: "Teacher / educator", icon: BookOpen },
    { value: "developer", label: "Developer / engineer", icon: Code },
    { value: "curious", label: "Just exploring / not working", icon: Compass },
    { value: "other", label: "Other — type it", icon: PenTool },
  ],
};

// Cascading deep-dive: the niche question shown AFTER the profession (varies by role).
const NICHE_STEPS = {
  student: {
    key: "niche", title: "What are you studying?", help: "We'll tailor the examples, tools and projects to your field.",
    options: [
      { value: "engineering", label: "Engineering / CS", icon: Cpu },
      { value: "commerce", label: "Commerce / Business", icon: BarChart3 },
      { value: "science", label: "Science", icon: FlaskConical },
      { value: "arts", label: "Arts & Humanities", icon: Palette },
      { value: "medical", label: "Medical / Health", icon: HeartPulse },
      { value: "law", label: "Law", icon: Scale },
      { value: "other", label: "Other — type it", icon: PenTool },
    ],
  },
  worker: {
    key: "niche", title: "Which function do you work in?", help: "So we focus AI on the exact tasks you do every day.",
    options: [
      { value: "marketing", label: "Marketing", icon: Megaphone },
      { value: "sales", label: "Sales / Business dev", icon: Target },
      { value: "finance", label: "Finance / Accounts", icon: Calculator },
      { value: "operations", label: "Operations", icon: Workflow },
      { value: "hr", label: "HR / People", icon: Users },
      { value: "tech", label: "Tech / Engineering", icon: Code },
      { value: "support", label: "Customer support", icon: Headphones },
      { value: "other", label: "Other — type it", icon: PenTool },
    ],
  },
  professional: {
    key: "niche", title: "What's your profession?", help: "We'll tailor AI to your field's real, high-stakes work — accuracy first.",
    options: [
      { value: "doctor", label: "Doctor / Healthcare", icon: HeartPulse },
      { value: "ca", label: "CA / Accountant", icon: Calculator },
      { value: "lawyer", label: "Lawyer / Legal", icon: Scale },
      { value: "architect", label: "Architect / Design", icon: Compass },
      { value: "engineer_pro", label: "Engineer (licensed)", icon: Cpu },
      { value: "consultant_pro", label: "Consultant / Analyst", icon: Briefcase },
      { value: "professor_pro", label: "Professor / Researcher", icon: GraduationCap },
      { value: "other", label: "Other — type it", icon: PenTool },
    ],
  },
  business: {
    key: "niche", title: "What sector is your business in?", help: "We'll show AI wins specific to your kind of business.",
    options: [
      { value: "retail", label: "Retail / Shop", icon: Store },
      { value: "food", label: "Food & Restaurant", icon: UtensilsCrossed },
      { value: "services", label: "Services", icon: Wrench },
      { value: "manufacturing", label: "Manufacturing", icon: Factory },
      { value: "ecommerce", label: "E-commerce / Online", icon: ShoppingCart },
      { value: "realestate", label: "Real estate", icon: Home },
      { value: "other", label: "Other — type it", icon: PenTool },
    ],
  },
  freelancer: {
    key: "niche", title: "What service do you offer?", help: "We'll center the path on delivering this kind of work.",
    options: [
      { value: "writing_svc", label: "Writing / Content", icon: FileText },
      { value: "design", label: "Design", icon: Palette },
      { value: "development", label: "Development", icon: Code },
      { value: "marketing", label: "Marketing", icon: Megaphone },
      { value: "video", label: "Video / Editing", icon: Video },
      { value: "consulting", label: "Consulting", icon: Briefcase },
      { value: "other", label: "Other — type it", icon: PenTool },
    ],
  },
  creative: {
    key: "niche", title: "What's your main medium?", help: "We'll prioritise the creative AI tools for your craft.",
    options: [
      { value: "writing_svc", label: "Writing", icon: PenTool },
      { value: "music", label: "Music", icon: Music },
      { value: "video", label: "Video / Film", icon: Video },
      { value: "design", label: "Design", icon: Palette },
      { value: "photography", label: "Photography", icon: Camera },
      { value: "art", label: "Art / Illustration", icon: Brush },
      { value: "other", label: "Other — type it", icon: PenTool },
    ],
  },
  trade: {
    key: "niche", title: "What's your trade or service?", help: "We'll keep it practical and jargon-free for your work.",
    options: [
      { value: "salon", label: "Salon / Beauty", icon: Scissors },
      { value: "food", label: "Food / Catering", icon: UtensilsCrossed },
      { value: "retail", label: "Shop / Retail", icon: Store },
      { value: "repair", label: "Repair / Technician", icon: Wrench },
      { value: "construction", label: "Construction", icon: Hammer },
      { value: "transport", label: "Transport / Logistics", icon: Truck },
      { value: "other", label: "Other — type it", icon: PenTool },
    ],
  },
  teacher: {
    key: "niche", title: "Who do you teach?", help: "We'll shape the path around your teaching context.",
    options: [
      { value: "school", label: "School students", icon: BookOpen },
      { value: "college", label: "College / University", icon: GraduationCap },
      { value: "coaching", label: "Coaching / Test-prep", icon: Target },
      { value: "corporate", label: "Corporate training", icon: Building2 },
      { value: "other", label: "Other — type it", icon: PenTool },
    ],
  },
  developer: {
    key: "niche", title: "What do you build?", help: "So we go technical in exactly the right direction.",
    options: [
      { value: "web", label: "Web development", icon: Globe2 },
      { value: "mobile", label: "Mobile apps", icon: Smartphone },
      { value: "dataml", label: "Data / ML", icon: Database },
      { value: "devops", label: "DevOps / Cloud", icon: Server },
      { value: "backend", label: "Backend / APIs", icon: Cpu },
      { value: "other", label: "Other — type it", icon: PenTool },
    ],
  },
};

const ambitionStep = (profession) => ({
  key: "ambition", free: true,
  title: profession === "business" ? "Where do you want to take your business?" : "What's your big goal with AI?",
  help: "Optional — share your ambition and we'll keep it in mind while shaping your path.",
  placeholder: profession === "business"
    ? "e.g. open a second outlet, sell online nationwide, automate operations…"
    : "e.g. land high-value clients, build my own product, double my output…",
});

const DEPTH = {
  key: "depth", title: "How deep do you want to go?", help: "We'll tune how much theory vs hands-on you get.",
  options: [
    { value: "practical", label: "Just practical tools (skip theory)", icon: Rocket },
    { value: "balanced", label: "A balance", icon: Gauge },
    { value: "deep", label: "I want to understand it deeply", icon: Layers },
  ],
};

const TIME = {
  key: "time_per_week", title: "How much time per week?", help: "We'll size your path to fit your life.",
  options: [
    { value: "<2", label: "Under 2 hours", icon: Clock },
    { value: "2-5", label: "2–5 hours", icon: Clock },
    { value: "5-10", label: "5–10 hours", icon: Clock },
    { value: "10+", label: "10+ hours", icon: Clock },
  ],
};

const LANGUAGE = {
  key: "language", title: "Which language do you prefer?", help: "We'll only show lessons genuinely in your language.",
  options: [
    { value: "english", label: "English", icon: Languages },
    { value: "hindi", label: "Hindi", icon: Languages },
    { value: "hinglish", label: "Hinglish", icon: Languages },
  ],
};

const CREATOR = {
  key: "creator_pref", title: "Whose teaching do you prefer?", help: "Some learners love Indian creators; some want international ones; many want a mix. You can switch anytime.",
  options: [
    { value: "indian", label: "Indian creators only", icon: Flag },
    { value: "global", label: "Global (international) creators", icon: Globe },
    { value: "both", label: "A mix of both — best of both worlds", icon: Layers },
  ],
};

const CODING = {
  key: "coding_comfort", title: "How comfortable are you with coding?", help: "This shapes how technical we go.",
  options: [
    { value: "none", label: "Not at all", icon: Sprout },
    { value: "little", label: "A little", icon: Terminal },
    { value: "comfortable", label: "Comfortable", icon: Code },
  ],
};

const SKIP_AHEAD = {
  key: "skip_ahead", title: "Where should we start you?", help: "No one's forced through content below their level.",
  options: [
    { value: false, label: "Start me from the fundamentals", icon: Sprout },
    { value: true, label: "I already know the basics — skip ahead", icon: FastForward },
  ],
};

const OTHER_TEXT_KEY = { profession: "profession_text", niche: "niche_text" };

function buildSteps(answers) {
  const steps = [KNOWLEDGE, GOAL, PROFESSION];
  const niche = NICHE_STEPS[answers.profession];
  if (niche) steps.push(niche);
  if (["business", "freelancer"].includes(answers.profession)) steps.push(ambitionStep(answers.profession));
  steps.push(DEPTH, TIME, LANGUAGE, CREATOR, CODING);
  if (["new", "sometimes", "comfortable"].includes(answers.knowledge)) steps.push(SKIP_AHEAD);
  return steps;
}

export default function Quiz() {
  const navigate = useNavigate();
  const { setUser, user } = useAuth();
  const [step, setStep] = useState(0);
  const [answers, setAnswers] = useState({});
  const [otherText, setOtherText] = useState("");
  const [ambitionText, setAmbitionText] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [coverage, setCoverage] = useState({});

  const advancing = useRef(false);
  const steps = buildSteps(answers);
  const q = steps[Math.min(step, steps.length - 1)];
  const progress = Math.round((step / steps.length) * 100);
  const current = answers[q.key];
  const otherKey = OTHER_TEXT_KEY[q.key];
  const showOtherInput = otherKey && current === "other";

  useEffect(() => {
    api.get("/languages").then(({ data }) => {
      const m = {};
      (data.languages || []).forEach((l) => { m[l.code] = l.topics_covered; });
      setCoverage(m);
    }).catch(() => {});
  }, []);

  // when arriving on the ambition step, prefill any prior answer
  useEffect(() => {
    if (q.free) setAmbitionText(answers.ambition || "");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step]);

  const goNext = (next) => {
    const isLast = step >= buildSteps(next).length - 1;
    if (isLast) { submit(next); return; }
    setTimeout(() => { setStep((s) => s + 1); advancing.current = false; }, 200);
  };

  const selectSingle = (value) => {
    if (advancing.current) return;
    // an "other" option needs a typed value before advancing
    if (otherKey && value === "other") {
      setOtherText(answers[otherKey] || "");
      setAnswers({ ...answers, [q.key]: "other" });
      return;
    }
    advancing.current = true;
    const next = { ...answers, [q.key]: value };
    // changing the profession invalidates a previously-chosen niche/ambition
    if (q.key === "profession") { delete next.niche; delete next.niche_text; delete next.ambition; }
    setAnswers(next);
    goNext(next);
  };

  const submitOther = () => {
    if (!otherText.trim()) { toast.message("Type it, or pick one above."); return; }
    if (advancing.current) return;
    advancing.current = true;
    const next = { ...answers, [q.key]: "other", [otherKey]: otherText.trim() };
    setAnswers(next);
    goNext(next);
  };

  const goAmbition = (skip) => {
    if (advancing.current) return;
    advancing.current = true;
    const next = { ...answers };
    if (!skip && ambitionText.trim()) next.ambition = ambitionText.trim();
    else delete next.ambition;
    setAnswers(next);
    goNext(next);
  };

  const submit = async (payload) => {
    const required = buildSteps(payload).map((s) => s.key).filter((k) => k !== "ambition");
    const missing = required.find((k) => payload[k] === undefined || payload[k] === null);
    if (missing) {
      advancing.current = false;
      setStep(buildSteps(payload).findIndex((s) => s.key === missing));
      toast.message("Just one more — please pick an option here.");
      return;
    }
    setSubmitting(true);
    // Anonymous learners: stash answers and finish after they create an account.
    if (!user) {
      savePendingQuiz(payload);
      trackAppEvent("explored_quiz");
      navigate("/register", { replace: true });
      return;
    }
    try {
      await api.post("/quiz/submit", payload);
      trackLead();
      trackAppEvent("explored_quiz");
      setUser({ ...user, has_path: true, language: payload.language });
      const unlocked = user?.is_subscribed || user?.role === "admin";
      toast.success(unlocked ? "Your personalized path is ready!" : "Your personalized path is ready — unlock it below!");
      navigate(unlocked ? "/dashboard" : "/subscribe", { replace: true });
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
      setSubmitting(false);
    }
  };

  const langBadge = (value) => {
    if (q.key !== "language") return null;
    const n = coverage[value];
    if (n === undefined) return null;
    if (n <= 0) return <span className="font-mono text-[10px] font-bold uppercase bg-brand-tangerine text-brand-ink border border-border px-1.5 py-0.5">soon</span>;
    return <span className="font-mono text-[10px] font-bold uppercase bg-brand-cyber text-white border border-border px-1.5 py-0.5">{n}</span>;
  };

  return (
    <div className="min-h-screen bg-background grid-paper flex flex-col">
      <header className="sticky top-0 z-20 bg-background border-b border-border">
        <div className="max-w-3xl mx-auto px-4 h-16 flex items-center justify-between">
          <Logo />
          <span className="font-mono text-xs font-bold uppercase tracking-widest" data-testid="quiz-progress-text">
            {step + 1} / {steps.length}
          </span>
        </div>
        <div className="h-2 bg-secondary border-t border-border">
          <motion.div className="h-full bg-brand-lime" animate={{ width: `${progress}%` }} transition={{ ease: [0.22, 1, 0.36, 1] }} />
        </div>
      </header>

      <main className="flex-1 flex items-center justify-center px-4 py-10">
        <div className="w-full max-w-3xl">
          {submitting ? (
            <div className="text-center py-20" data-testid="quiz-building">
              <div className="inline-grid place-items-center h-16 w-16 bg-brand-lime border border-border shadow-brutal mx-auto">
                <Loader2 className="h-8 w-8 animate-spin text-foreground" />
              </div>
              <h2 className="mt-6 font-heading font-extrabold tracking-tight text-3xl">Assembling your path…</h2>
              <p className="mt-2 text-muted-foreground font-medium">Hand-picking modules and teachers just for you.</p>
            </div>
          ) : (
            <AnimatePresence mode="wait">
              <motion.div
                key={step}
                initial={{ opacity: 0, x: 40 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -40 }}
                transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
              >
                {step > 0 && (
                  <button data-testid="quiz-back" onClick={() => { advancing.current = false; setStep((s) => s - 1); }} className="inline-flex items-center gap-1 font-mono text-xs font-bold uppercase tracking-wider text-muted-foreground hover:text-foreground mb-6">
                    <ArrowLeft className="h-4 w-4" /> Back
                  </button>
                )}
                <p className="font-mono text-xs font-bold uppercase tracking-widest text-brand-pink">Step {step + 1}</p>
                <h1 className="mt-2 font-heading font-extrabold tracking-tight text-3xl md:text-4xl leading-[0.95]">{q.title}</h1>
                <p className="mt-3 text-muted-foreground font-medium">{q.help}</p>

                {q.free ? (
                  <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} className="mt-8">
                    <Input
                      data-testid="ambition-input"
                      autoFocus
                      value={ambitionText}
                      onChange={(e) => setAmbitionText(e.target.value)}
                      onKeyDown={(e) => e.key === "Enter" && goAmbition(false)}
                      placeholder={q.placeholder}
                      className="h-12 rounded-lg border border-border focus-visible:ring-2 focus-visible:ring-primary/40"
                    />
                    <div className="mt-4 flex items-center gap-3">
                      <Button data-testid="ambition-continue" onClick={() => goAmbition(false)} className="bg-primary text-primary-foreground h-12 px-7">
                        Continue <ArrowRight className="h-4 w-4" />
                      </Button>
                      <button data-testid="ambition-skip" onClick={() => goAmbition(true)} className="text-sm font-semibold text-muted-foreground hover:text-foreground">
                        Skip
                      </button>
                    </div>
                  </motion.div>
                ) : (
                  <>
                    <div className="mt-8 grid sm:grid-cols-2 gap-3">
                      {q.options.map((opt, i) => {
                        const Icon = opt.icon;
                        const selected = current === opt.value;
                        return (
                          <motion.button
                            key={String(opt.value)}
                            data-testid={`quiz-option-${q.key}-${opt.value}`}
                            onClick={() => selectSingle(opt.value)}
                            initial={{ opacity: 0, y: 12 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ delay: Math.min(i * 0.03, 0.3) }}
                            className={`group flex items-center gap-4 border border-border p-4 text-left transition-all duration-150 ${
                              selected ? "bg-brand-lime shadow-brutal -translate-x-0.5 -translate-y-0.5" : "bg-card shadow-brutal-sm brutal-press"
                            }`}
                          >
                            <span className={`grid place-items-center h-11 w-11 shrink-0 border border-border ${selected ? "bg-foreground text-brand-lime" : "bg-secondary text-foreground"}`}>
                              <Icon className="h-5 w-5" />
                            </span>
                            <span className="font-heading font-bold text-base flex-1">{opt.label}</span>
                            {langBadge(opt.value)}
                            <ArrowRight className={`h-5 w-5 transition-all ${selected ? "opacity-100" : "text-muted-foreground -translate-x-1 opacity-0 group-hover:opacity-100 group-hover:translate-x-0"}`} />
                          </motion.button>
                        );
                      })}
                    </div>

                    {showOtherInput && (
                      <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} className="mt-4 flex flex-col sm:flex-row gap-3">
                        <Input
                          data-testid={`${q.key}-text-input`}
                          autoFocus
                          value={otherText}
                          onChange={(e) => setOtherText(e.target.value)}
                          onKeyDown={(e) => e.key === "Enter" && submitOther()}
                          placeholder={q.key === "profession" ? "e.g. wedding photographer, dietician, real-estate agent…" : "Tell us your specific focus…"}
                          className="h-12 rounded-lg border border-border focus-visible:ring-2 focus-visible:ring-primary/40 flex-1"
                        />
                        <Button data-testid={`${q.key}-text-continue`} onClick={submitOther} className="bg-primary text-primary-foreground h-12 px-7">
                          Continue <ArrowRight className="h-4 w-4" />
                        </Button>
                      </motion.div>
                    )}
                  </>
                )}
              </motion.div>
            </AnimatePresence>
          )}
        </div>
      </main>
    </div>
  );
}
